import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { useAuthCtx } from '../App';
import api from '../utils/api';

// Where Wallet Plug sends a member back after the hosted VIP checkout. The
// `status` query param is a UX hint only — the trusted result comes from asking
// our backend to verify the payment (Wallet Plug verify, or the IPN webhook that
// activates the membership server-side). On success we poll a few times because
// the processor may settle a beat after the redirect.

const POLL_INTERVAL_MS = 2500;
const MAX_ATTEMPTS = 6;

export default function MembershipReturnPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuthCtx();
  const hint = params.get('status') || 'success';

  // confirming | pending | failed | cancelled | error
  const [state, setState] = useState(hint === 'success' ? 'confirming' : hint);
  const attempts = useRef(0);
  const timer = useRef(null);

  const confirm = useCallback(async () => {
    try {
      // Two ways the membership becomes active: (1) our best-effort verify-on-
      // return, or (2) Wallet Plug's IPN webhook activating it server-side.
      const { data } = await api.post(`/memberships/${id}/walletplug/confirm`);
      if (data.active) {
        await refreshUser();   // VIP pricing lights up site-wide
        navigate('/membership', { replace: true });
        return;
      }
      attempts.current += 1;
      if (attempts.current >= MAX_ATTEMPTS) { setState('pending'); return; }
      timer.current = setTimeout(confirm, POLL_INTERVAL_MS);
    } catch {
      attempts.current += 1;
      if (attempts.current >= MAX_ATTEMPTS) { setState('error'); return; }
      timer.current = setTimeout(confirm, POLL_INTERVAL_MS);
    }
  }, [id, navigate, refreshUser]);

  useEffect(() => {
    if (hint === 'success') confirm();
    return () => clearTimeout(timer.current);
  }, [hint, confirm]);

  const body = {
    confirming: {
      title: 'Confirming your membership…',
      text: 'Hang tight — we’re verifying your payment with the processor.',
      spinner: true,
      cta: null,
    },
    pending: {
      title: 'Payment is processing',
      text: 'Your payment hasn’t fully cleared yet. Your VIP access will switch on as soon as it does — no need to pay again.',
      cta: { to: '/account', label: 'Go to my account' },
    },
    failed: {
      title: 'Payment failed',
      text: 'Your payment didn’t go through. You can try joining again.',
      cta: { to: '/membership', label: 'Back to membership' },
    },
    cancelled: {
      title: 'Payment cancelled',
      text: 'You cancelled the payment. You can join VIP whenever you’re ready.',
      cta: { to: '/membership', label: 'Back to membership' },
    },
    error: {
      title: 'Couldn’t confirm automatically',
      text: 'We couldn’t confirm the payment status right now. If you completed payment, your VIP access will appear shortly.',
      cta: { to: '/account', label: 'Go to my account' },
    },
  }[state] || {};

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-lg mx-auto px-4 py-20 w-full text-center">
        {body.spinner && (
          <div className="mx-auto mb-6 h-10 w-10 rounded-full border-4 border-tea-200 border-t-tea-600 animate-spin" />
        )}
        <h1 className="text-2xl font-bold mb-3">{body.title}</h1>
        <p className="text-gray-600 mb-8">{body.text}</p>
        {body.cta && (
          <Link to={body.cta.to} className="btn-primary inline-block px-6 py-2">
            {body.cta.label}
          </Link>
        )}
      </main>
      <Footer />
    </div>
  );
}
